import { useCallback, useEffect, useId, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useSaveStatusReporter } from "@/components/settings/SaveStatus";
import { notify } from "@/components/shared/AppToast";
import { t } from "@/localization";

// Saves a Formik form shortly after the user stops changing it.
//   <AutoSave {...formik} />
//
// - Saves only when the values differ from what was last saved (or loaded), so
//   loading or re-initialising a form never saves it.
// - Invalid values are not sent: the invalid fields show their errors and the
//   header says "Not saved: <first error>". Other fields keep their values.
// - One save at a time; changes made during a save are saved right after it.
// - A pending save is sent when the screen closes.
// - onSubmit must throw when saving fails; the header then shows
//   "Couldn't save · Retry".
// Status goes to the screen's SaveStatusProvider; without one (e.g. Profile)
// it falls back to a toast.

const stable = (values) => {
  try {
    return JSON.stringify(values ?? null);
  } catch (e) {
    return String(Math.random());
  }
};

// { links: [{ url: "x" }] } → { links: [{ url: true }] } for Formik's touched.
const touchedFrom = (errors) => {
  if (Array.isArray(errors))
    return errors.map((e) => (e ? touchedFrom(e) : undefined));
  if (errors && typeof errors === "object") {
    return Object.fromEntries(
      Object.entries(errors).map(([k, v]) => [k, touchedFrom(v)])
    );
  }
  return true;
};

const firstError = (errors) => {
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  for (const value of Object.values(errors)) {
    const found = firstError(value);
    if (found) return found;
  }
  return null;
};

const errorText = (err) =>
  err?.errors?.[0]?.message ||
  err?.message ||
  (typeof err === "string" ? err : t("Please try again."));

const AutoSave = ({
  debounceMs = 800,
  submitForm,
  validateForm,
  setTouched,
  touched,
  values,
  initialValues,
  statusId,
}) => {
  const autoId = useId();
  const report = useSaveStatusReporter(statusId || autoId);
  const lastSaved = useRef(stable(initialValues));
  const latest = useRef(values);
  latest.current = values;
  const latestTouched = useRef(touched);
  latestTouched.current = touched;
  const inFlight = useRef(false);
  const again = useRef(false);

  // A re-initialised form (new initialValues from the server) is the new baseline.
  const initialKey = stable(initialValues);
  useEffect(() => {
    lastSaved.current = initialKey;
  }, [initialKey]);

  const save = useCallback(async () => {
    const snapshot = stable(latest.current);
    if (snapshot === lastSaved.current) {
      report?.(null);
      return;
    }
    if (inFlight.current) {
      again.current = true;
      return;
    }
    const errors = validateForm ? await validateForm(latest.current) : {};
    if (errors && Object.keys(errors).length) {
      setTouched?.(
        { ...(latestTouched.current || {}), ...touchedFrom(errors) },
        false
      );
      const message = firstError(errors);
      if (report) report({ state: "invalid", message, at: Date.now() });
      return;
    }
    inFlight.current = true;
    report?.({ state: "saving", at: Date.now() });
    try {
      await submitForm();
      lastSaved.current = snapshot;
      if (report) report({ state: "saved", at: Date.now() });
      else notify.success(t("Saved"));
    } catch (err) {
      console.error(err);
      if (report)
        report({
          state: "error",
          message: errorText(err),
          retry: () => save(),
          at: Date.now(),
        });
      else notify.error(t("Couldn't save your changes"), errorText(err));
    } finally {
      inFlight.current = false;
      if (again.current) {
        again.current = false;
        save();
      }
    }
  }, [report, submitForm, validateForm, setTouched]);

  const debounced = useDebouncedCallback(save, debounceMs);

  const valuesKey = stable(values);
  useEffect(() => {
    if (valuesKey === lastSaved.current) return;
    report?.({ state: "pending", at: Date.now() });
    debounced();
  }, [valuesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the screen: send what's pending instead of dropping it.
  useEffect(() => () => debounced.flush(), [debounced]);

  return null;
};

export default AutoSave;
